import cn from 'classnames';
import { useI18next } from 'gatsby-plugin-react-i18next';
import React, { useMemo, useState } from 'react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import {
  addLanguagesWithNames,
  getLanguagesFromPath,
  getLocalizedPath,
  languageToNames,
} from '../../utils/localization';
import Button from '../Button';
import Global from './Icons/global.svg';
import { Link } from 'gatsby';
interface LanguageSwitcherProps {
  localesAvailable?: { language: string; url: string; '@type': string }[];
}
interface Language {
  language: string;
  languageCode: string;
  slug?: string;
  localized?: boolean;
  pageType?: string;
}

const hasLanguage = (languageCode: string, languages: Language[]): boolean => {
  return languages?.some(language => language.languageCode === languageCode);
};

const addMissingLanguages = (
  updatedArray: Language[],
  languages: Language[]
): Language[] => {
  return languages.reduce((accumulator, language) => {
    if (!hasLanguage(language.languageCode, updatedArray)) {
      accumulator?.push(language);
    }
    return accumulator;
  }, updatedArray?.slice());
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = props => {
  const { localesAvailable } = props;
  const { language, originalPath, changeLanguage } = useI18next();

  const triggerRef = React.useRef(null);
  const dropperRef = React.useRef(null);
  const [langDropDown, setLangDropDown] = useState<boolean>(false);
  useOnClickOutside([triggerRef, dropperRef], () => setLangDropDown(false));

  let allowedLanguages = useMemo(() => {
    return originalPath && getLanguagesFromPath(originalPath);
  }, []);

  if (originalPath.includes('/blog') && localesAvailable !== undefined) {
    allowedLanguages = localesAvailable.filter(item => item.language !== 'ja');
  }

  const languagesWithNames = addLanguagesWithNames(allowedLanguages);

  const defaultLanguages = Object.entries(languageToNames).map(
    ([key, value]) => ({
      languageCode: key,
      language: value,
    })
  );

  const finalLanguages: Language[] = languagesWithNames
    ? addMissingLanguages(languagesWithNames, defaultLanguages)
    : defaultLanguages;

  return (
    <>
      <div ref={dropperRef} className="flex items-center relative">
        <Button
          onClick={() => {
            setLangDropDown(!langDropDown);
          }}
          className="bg-transparent border-none flex items-center placeholder-gray-3 !p-0 lg:px-4 py-3 h-10"
        >
          <Global />

          <div
            ref={triggerRef}
            className="ml-2 font-medium text-gray-3-dark hover:opacity-80 transition"
          >
            <span className="pt-1">{languageToNames[language]}</span>
          </div>
          {langDropDown && (
            <ul
              className={cn(
                'border absolute p-3 transition border-gray-4-light ease-in duration-100 mt-1 py-2 -left-[7px] md:left-0 bg-white overflow-hidden rounded-md bottom-[60px]',
                langDropDown
                  ? 'opacity-100 z-50 shadow-2xl translate-y-1'
                  : 'opacity-0 -z-20 translate-y-0'
              )}
            >
              {finalLanguages.map((lng, i) => {
                if (lng.language) {
                  return (
                    <React.Fragment key={lng.languageCode}>
                      <li
                        className={cn(
                          'px-3 space-x-3 text-base font-medium rounded-lg transition-all hover:text-indigo-1 hover:bg-gray-6-light text-left'
                        )}
                      >
                        <Link
                          to={getLocalizedPath(lng, originalPath)}
                          className="block py-3"
                          onClick={() =>
                            lng.localized &&
                            lng.pageType !== 'blog' &&
                            changeLanguage(lng.languageCode)
                          }
                        >
                          {lng.language}
                        </Link>
                      </li>
                    </React.Fragment>
                  );
                }
              })}
            </ul>
          )}
        </Button>
      </div>
    </>
  );
};

export default LanguageSwitcher;
